import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  MapPin,
  Bed,
  Bath,
  Home,
  Users,
  User,
  Pencil,
  Trash2,
  Check,
  Plus,
  HelpCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import AccommodationModal from "@/components/modal/AccommodationModal";
import DeleteConfirmDialog from "@/components/modal/DeleteConfirmDialog";

const API_URL = import.meta.env.VITE_API_URL;

function ManageAccommodations({ openAddFromDashboard, onAddHandled }) {
  const [filterStatus, setFilterStatus] = useState("All");
  const [active, setActive] = useState(null);

  // UX / modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [mobileListOpen, setMobileListOpen] = useState(false);

  const [openManageGuide, setOpenManageGuide] = useState(false);

  // Images carousel (IG-style)
  const imagesRef = useRef(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const scrollToImage = (idx) => {
    const el = imagesRef.current;
    if (!el) return;
    const max = (active?.images || []).length - 1;
    const next = Math.max(0, Math.min(idx, max));
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    setActiveImageIndex(next);
  };

  const handleImagesScroll = () => {
    const el = imagesRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveImageIndex(idx);
  };

  const queryClient = useQueryClient();

  const normalizeAccommodations = (raw) => {
    const baseUrl = (API_URL || "http://localhost:5000").replace(/\/$/, "");

    return (raw || []).map((acc) => {
      const normalizedImages = (acc.images || [])
        .map((img) => {
          if (!img) return null;

          if (typeof img === "string") {
            if (/^https?:\/\//.test(img)) return img;
            const path = img.startsWith("/") ? img : `/${img}`;
            return `${baseUrl}${path}`;
          }

          const candidate =
            img.url || img.path || img.image_url || img.imageUrl || "";

          if (!candidate) return null;
          if (/^https?:\/\//.test(candidate)) return candidate;

          const path = candidate.startsWith("/") ? candidate : `/${candidate}`;
          return `${baseUrl}${path}`;
        })
        .filter(Boolean);

      return { ...acc, images: normalizedImages };
    });
  };

  const fetchAccommodations = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");

    const res = await fetch(`${API_URL}/api/sdaowner/get_accommodations`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message || data.msg || "Failed to fetch accommodations"
      );
    }

    return normalizeAccommodations(data.accommodations || []);
  };

  const { data: accommodations = [], isLoading } = useQuery({
    queryKey: ["accommodations"],
    queryFn: fetchAccommodations,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // 👉 List AFTER applying the Available / Occupied filter
  const filteredAccommodations = useMemo(() => {
    return accommodations.filter((a) => {
      const status = (a.status || "").toLowerCase();
      const listing = (
        a.listing_status ||
        a.listingStatus ||
        "listed"
      ).toLowerCase();

      if (filterStatus === "All") return true;

      if (filterStatus === "Available") {
        return status === "available" || status === "vacant";
      }

      if (filterStatus === "Occupied") {
        return status === "occupied";
      }

      if (filterStatus === "Listed") {
        return listing === "listed";
      }

      if (filterStatus === "Unlisted") {
        return listing === "unlisted";
      }

      if (filterStatus === "In progress") {
        return listing === "in_progress";
      }

      return true;
    });
  }, [accommodations, filterStatus]);

  // ✅ keep active stable when data refreshes (and respect current filter)
  useEffect(() => {
    if (!filteredAccommodations.length) {
      setActive(null);
      setActiveImageIndex(0);
      return;
    }

    setActive((prev) => {
      if (!prev) {
        setActiveImageIndex(0);
        return filteredAccommodations[0];
      }
      const match = filteredAccommodations.find((acc) => acc.id === prev.id);
      if (!match) setActiveImageIndex(0);
      return match || filteredAccommodations[0];
    });
  }, [filteredAccommodations]);

  const kpis = useMemo(() => {
    const totalProperties = accommodations.length;

    // Define what counts as vacant vs occupied in your domain:
    const vacantUnits = accommodations.filter((a) => {
      const s = (a.status || "").toLowerCase();
      return s === "vacant" || s === "available";
    }).length;

    const occupiedUnits = accommodations.filter((a) => {
      const s = (a.status || "").toLowerCase();
      return s === "occupied";
    }).length;

    // occupancyRate computed as occupied / total (0..100)
    const occupancyRate =
      totalProperties === 0
        ? 0
        : Math.round((occupiedUnits / totalProperties) * 100);

    return {
      totalProperties,
      vacantUnits,
      occupiedUnits,
      occupancyRate,
    };
  }, [accommodations]);

  // open the Add Accommodation modal when navigated from dashboard
  useEffect(() => {
    if (openAddFromDashboard) {
      // ensure it's in "add" mode
      setSelectedAccommodation(null);
      setOpenModal(true);

      // tell the portal we've handled it so it doesn't reopen on every render
      if (onAddHandled) {
        onAddHandled();
      }
    }
  }, [openAddFromDashboard, onAddHandled]);

  // Handlers
  const handleAddNew = () => {
    setSelectedAccommodation(null);
    setOpenModal(true);
  };

  const handleEdit = (accommodation) => {
    setSelectedAccommodation(accommodation);
    setOpenModal(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("No access token found for delete");
        return;
      }

      const res = await fetch(
        `${API_URL}/api/sdaowner/delete_accommodation/${selectedId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();

      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      } else {
        console.error(
          "Delete failed:",
          data.message || data.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error deleting accommodation:", error);
    } finally {
      setOpenDeleteDialog(false);
      setSelectedId(null);
    }
  };

  // derive status values safely (handles when `active` is null)
  const occupancyStatus = (active?.status || "").toLowerCase();
  const listingStatus = (
    active?.listing_status ||
    active?.listingStatus ||
    "listed"
  ).toLowerCase();

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-50 gap-4 lg:gap-8 w-full lg:overflow-hidden">
      {/* Sidebar */}
      <Card className="hidden lg:flex w-80 bg-white border-r border-gray-200 flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl text-gray-900">Property</h2>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[#D2138C] text-white rounded-lg hover:bg-[#B01076] transition-colors"
                type="button"
                onClick={handleAddNew}>
                <Plus size={16} />
                Add property
              </button>
            </div>
          </div>

          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none bg-white cursor-pointer">
              <option value="All">All</option>
              <option value="Available">Available</option>
              <option value="Occupied">Occupied</option>
              <option value="Listed">Listed</option>
              <option value="Unlisted">Unlisted</option>
              <option value="In progress">In progress</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-hidden lg:overflow-hidden">
          <ScrollArea className="h-[320px] lg:h-full">
            <div className="space-y-0">
              {filteredAccommodations.map((p) => {
                const isSelected = active?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActive(p)}
                    aria-selected={isSelected}
                    className={`w-full text-left p-4 border-b border-gray-200 transition-colors flex items-start gap-3 ${
                      isSelected
                        ? "bg-pink-50 border-l-4 border-l-[#D2138C]"
                        : "hover:bg-gray-50"
                    }`}>
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Home size={20} className="text-[#D2138C]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-gray-900 truncate">{p.title}</h3>
                        <span className="ml-2 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                          {p.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {p.location}
                      </p>
                      <p className="text-sm text-gray-600 truncate">{p.type}</p>
                      <p className="text-sm text-gray-900 mt-1">
                        Capacity: {p.capacity}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </Card>

      {/* Right Column - MAIN (no outer padding to make it flush) */}
      <div className="flex-1 px-4 lg:px-0 pb-6 order-1 lg:order-2 flex flex-col min-h-0">
        {/* Stats Bar — reduced padding to avoid large horizontal margin */}
        <div className="px-0 lg:px-4 py-4 max-w-full">
          {/* Mobile: Selected Property + actions */}
          <div className="lg:hidden mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-gray-600">Selected Property</p>
              <p className="text-sm text-gray-900 font-medium truncate">
                {active?.title || "None"}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm bg-[#D2138C] text-white rounded-lg hover:bg-[#B01076] transition-colors"
                type="button"
                onClick={handleAddNew}>
                <Plus size={16} />
                Add
              </button>

              <button
                type="button"
                onClick={() => setMobileListOpen(true)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Select
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Properties</p>
              <p className="text-3xl text-gray-900">{kpis.totalProperties}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Vacant Units</p>
              <p className="text-3xl text-gray-900">{kpis.vacantUnits}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Occupied Units</p>
              <p className="text-3xl text-gray-900">{kpis.occupiedUnits}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Occupancy Rate</p>
              <p className="text-3xl text-gray-900">{kpis.occupancyRate}%</p>
            </div>
          </div>
        </div>

        <Card className="flex flex-col flex-1 min-h-0 lg:h-auto h-[calc(100vh-240px)]">
          <CardHeader className="pb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              {!active ? (
                <div className="text-gray-500">No accommodation selected</div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-3xl text-gray-900 truncate">
                      {active.title}
                    </h1>

                    {/* Occupancy status: Vacant / Occupied */}
                    <span
                      className={
                        "px-3 py-1 rounded-full text-sm " +
                        (occupancyStatus === "vacant"
                          ? "bg-green-100 text-green-700"
                          : occupancyStatus === "occupied"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-gray-100 text-gray-600")
                      }>
                      {active.status || "Unknown"}
                    </span>

                    {/* Listing status: Listed / Unlisted / In progress */}
                    <span
                      className={
                        "px-3 py-1 rounded-full text-sm " +
                        (listingStatus === "listed"
                          ? "bg-blue-100 text-blue-700"
                          : listingStatus === "unlisted"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-orange-100 text-orange-700")
                      }>
                      {listingStatus === "in_progress"
                        ? "In progress"
                        : listingStatus.charAt(0).toUpperCase() +
                          listingStatus.slice(1)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 mb-3">
                    <MapPin size={16} />
                    <span>{active.location}</span>
                  </div>

                  <div className="flex items-center gap-6 text-gray-700 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Bed size={18} />
                      <span>{active.bedrooms} beds</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bath size={18} />
                      <span>{active.bathrooms} baths</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home size={18} />
                      <span>{active.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={18} />
                      <span>{active.gender}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={18} />
                      <span>Capacity: {active.capacity}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpenManageGuide(true)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                aria-label="Guide"
                title="Guide">
                <HelpCircle size={20} className="text-[#D2138C]" />
              </button>

              <button
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Edit"
                type="button"
                onClick={() => active && handleEdit(active)}
                disabled={!active}>
                <Pencil size={20} className="text-gray-700" />
              </button>

              <button
                className="p-2 border border-gray-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
                type="button"
                onClick={() => active && handleDeleteClick(active.id)}
                disabled={!active}>
                <Trash2 size={20} className="text-gray-700" />
              </button>
            </div>
          </CardHeader>

          <CardContent className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {!active ? (
              <div className="text-center text-gray-500 py-20">
                Select an accommodation to view details
              </div>
            ) : (
              <div key={active.id}>
                <div className="mb-5">
                  <h2 className="text-xl text-gray-900 mb-3">
                    Property Description
                  </h2>
                  <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {active.description}
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="text-xl text-gray-900 mb-3">
                    Property Features
                  </h2>

                  {(active.features || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No features added.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(active.features || []).map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700">
                          <Check size={16} className="text-[#D2138C]" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <h2 className="text-xl text-gray-900 mb-3">
                    Property Amenities
                  </h2>

                  {(active.amenities || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No amenities added.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(active.amenities || []).map((amenity, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700">
                          <Check size={16} className="text-[#D2138C]" />
                          {amenity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <h2 className="text-xl text-gray-900 mb-3">
                    Property Images
                  </h2>

                  {(active.images || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No images uploaded.</p>
                  ) : (
                    <div className="relative">
                      {/* Left/Right buttons (desktop + optional mobile) */}
                      <button
                        type="button"
                        onClick={() => scrollToImage(activeImageIndex - 1)}
                        className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-gray-200 shadow hover:bg-white"
                        aria-label="Previous image">
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>

                      <button
                        type="button"
                        onClick={() => scrollToImage(activeImageIndex + 1)}
                        className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-gray-200 shadow hover:bg-white"
                        aria-label="Next image">
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>

                      {/* Swipe carousel */}
                      <div
                        ref={imagesRef}
                        onScroll={handleImagesScroll}
                        className="w-full aspect-video overflow-x-auto rounded-xl bg-gray-100 scroll-smooth snap-x snap-mandatory flex no-scrollbar"
                        style={{ WebkitOverflowScrolling: "touch" }}>
                        {(active.images || []).map((image, index) => (
                          <div
                            key={index}
                            className="w-full h-full flex-shrink-0 snap-center">
                            <img
                              src={image}
                              alt={`${active.title} - Image ${index + 1}`}
                              className="w-full h-full object-cover"
                              draggable="false"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Dots */}
                      <div className="mt-3 flex items-center justify-center gap-2">
                        {(active.images || []).map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => scrollToImage(idx)}
                            className={
                              "h-2 w-2 rounded-full transition " +
                              (idx === activeImageIndex
                                ? "bg-[#D2138C]"
                                : "bg-gray-300")
                            }
                            aria-label={`Go to image ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {openManageGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenManageGuide(false)}
            aria-label="Close guide"
          />

          {/* Modal */}
          <div className="relative z-10 w-[95%] max-w-2xl rounded-2xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Manage accommodations guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenManageGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
              <p>Use this page to add, update, and manage your properties.</p>

              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Click <span className="font-medium">Add property</span> to
                  create a new listing.
                </li>
                <li>
                  Use the{" "}
                  <span className="font-medium">Available / Occupied</span>{" "}
                  filter to view properties by status.
                </li>
                <li>
                  Select a property from the left list to view full details on
                  the right.
                </li>
                <li>
                  Use the <span className="font-medium">Edit</span> button to
                  update details, features, amenities, and images.
                </li>
                <li>
                  Use <span className="font-medium">Delete</span> to remove a
                  property you no longer manage.
                </li>
              </ul>

              <div className="text-xs text-gray-500">
                Tip: Keep status updated (Vacant/Occupied) so Availability and
                Booking reflect the correct info.
              </div>
            </div>

            <div className="border-t px-5 py-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOpenManageGuide(false)}
                className="rounded-xl bg-[#D2138C] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Select Property Modal */}
      {mobileListOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileListOpen(false)}
            aria-label="Close property list"
          />

          {/* Modal */}
          <div className="absolute left-1/2 top-1/2 w-[94%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 truncate">
                  Select property
                </h2>
                <p className="text-xs text-gray-500">
                  Choose a property to view details
                </p>
              </div>

              <button
                type="button"
                onClick={() => setMobileListOpen(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Filter */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-medium text-gray-900">Filter</p>

                <button
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-[#D2138C] text-white rounded-lg hover:bg-[#B01076] transition-colors"
                  type="button"
                  onClick={() => {
                    setMobileListOpen(false);
                    handleAddNew();
                  }}>
                  <Plus size={16} />
                  Add property
                </button>
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none bg-white cursor-pointer">
                <option value="All">All</option>
                <option value="Available">Available</option>
                <option value="Occupied">Occupied</option>
                <option value="Listed">Listed</option>
                <option value="Unlisted">Unlisted</option>
                <option value="In progress">In progress</option>
              </select>
            </div>

            {/* List */}
            <div className="max-h-[60vh] overflow-auto">
              {filteredAccommodations.map((p) => {
                const isSelected = active?.id === p.id;

                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setActive(p);
                      setMobileListOpen(false);
                    }}
                    className={`w-full text-left p-4 border-b border-gray-200 transition-colors flex items-start gap-3 ${
                      isSelected ? "bg-pink-50" : "hover:bg-gray-50"
                    }`}>
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Home size={20} className="text-[#D2138C]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-gray-900 truncate">{p.title}</h3>
                        <span className="ml-2 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                          {p.status}
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 truncate">
                        {p.location}
                      </p>
                      <p className="text-sm text-gray-600 truncate">{p.type}</p>
                      <p className="text-sm text-gray-900 mt-1">
                        Capacity: {p.capacity}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="p-4 flex justify-end">
              <button
                type="button"
                onClick={() => setMobileListOpen(false)}
                className="rounded-xl bg-[#D2138C] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AccommodationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        accommodation={selectedAccommodation}
        onSuccess={() =>
          queryClient.invalidateQueries({ queryKey: ["accommodations"] })
        }
      />

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Accommodation"
        description="Are you sure you want to delete this accommodation? This action cannot be undone."
      />
    </div>
  );
}

export default ManageAccommodations;
